import React, {
  useState, useRef, useEffect, useContext,
} from 'react';
import StoryblokClient from 'storyblok-js-client';
import { Props } from '../types';
import { PageStateContext, StoryblokService } from '../../services';

type CategoryListProps = Props<CategoriesListType> & {
  activeCategory?: string;
}

enum CategoryTypes {
  PRODUCT = 'productCategory',
  TAG = 'tagCategory',
  DID = 'didCategory',
  AKTUELLES = 'aktuellesCategory',
}

type CategoriesListType = {
  content_type: CategoryTypes;
};

const categoriesMap = {
  productCategory: {
    getUrl: (): string => '',
    component: 'ProductCategory',
  },
  tagCategory: {
    getUrl: (): string => '',
    component: 'Tag',
  },
  didCategory: {
    getUrl: (settings): string => settings?.did_overview_page_link?.story?.fullSlug || '',
    component: 'DidCategory',
  },
  aktuellesCategory: {
    getUrl: (settings): string => settings?.blog_overview_page_link?.story?.fullSlug || '',
    component: 'AktuellesCategory',
  },
};

export const CategoriesList = ({
  blok, slot, activeCategory,
}: CategoryListProps): JSX.Element => {
  const { settings } = useContext(PageStateContext);
  const storyblokJsClient = useRef(null);
  const contentType: CategoryTypes = blok.content_type;

  const list = useRef(null);
  const [categories, setCategories] = useState([]);

  const categoryUrl: string = (typeof (categoriesMap[contentType] || {}).getUrl === 'function'
    ? categoriesMap[contentType].getUrl(settings) : '').replace(/\/$/, '');

  useEffect(() => {
    storyblokJsClient.current = new StoryblokClient({
      accessToken: StoryblokService.getConfig().options.accessToken as string,
    });
  }, []);

  const getCategoriesFromStoryblok = async () => {
    const { data } = await storyblokJsClient.current?.getStory('', {
      filter_query: {
        component: {
          in: categoriesMap[contentType].component,
        },
      },
    });
    return data.stories;
  };

  const handleCategoryChange = (e): void => {
    const { identifier } = e.detail;
    setCategories((prev) => prev.map((item) => ({
      ...item,
      active: item.uuid === identifier,
    })));
  };

  useEffect(() => {
    getCategoriesFromStoryblok().then((result) => {
      setCategories(result);
    });
    document.addEventListener('rocheCategoryToggle', handleCategoryChange);

    return (): void => document.removeEventListener('rocheCategoryToggle', handleCategoryChange);
  }, [contentType]);

  useEffect(() => {
    if (!activeCategory) {
      setCategories((prev) => prev.map((item) => ({
        ...item,
        active: false,
      })));
    }
  }, [activeCategory]);

  return (
    <div ref={list} slot={slot}>
      {categories?.map((category) => (
        <roche-category
          key={category.uuid}
          identifier={category.uuid}
          type="outlined"
          active={category.active || category.uuid === activeCategory}
          text={category.name}
          theme={category.content?.theme}
          url={categoryUrl ? `/${categoryUrl}?categories=${category.uuid}` : '#'}
        />
      ))}
    </div>
  );
};
